import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  useTheme,
  Autocomplete,
  Grid,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import theme from "../../../assets/theme";
import { useLoading } from "../../../context/loading.context";
import { getPrintFormatInvoice, getSalesInvoiceDetails, getUnallocatedAmount, markInvoicepayment, printFile } from "../../../service/network";
import { useNotification } from "../../../context/notification.context";
import { Print } from "@mui/icons-material";

// Styled Components
const StyledTextField = ({ label, align = 'left', ...props }) => {
  const theme = useTheme();
  const inputBoxStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.borderRadius?.medium || 8,
      backgroundColor: theme.palette?.lightgray?.main || "#F3F4F6",
      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
      "& fieldset": {
        borderColor: theme.palette?.gray?.main || "#D1D5DB",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused": {
        backgroundColor: theme.palette?.light?.main || "#f8f8ff",
        "& fieldset": {
          borderColor: theme.palette.primary.main,
        },
      },
      "& input": {
        textAlign: align, // Add this line
      },
    },
  };

  return (
    <Box>
      {label && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <TextField size="small" fullWidth sx={inputBoxStyle} {...props} />
    </Box>
  );
};
const StyledAutocomplete = ({ label, options = [], ...props }) => {
  const theme = useTheme();
  const inputBoxStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.borderRadius?.medium || 8,
      backgroundColor: theme.palette?.lightgray?.main || "#F3F4F6",
      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
      "& fieldset": {
        borderColor: theme.palette?.gray?.main || "#D1D5DB",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused": {
        backgroundColor: theme.palette?.light?.main || "#f8f8ff",
        "& fieldset": {
          borderColor: theme.palette.primary.main,
        },
      },
    },
  };

  return (
    <Box>
      {label && (
        <Typography variant="bodyText1" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Autocomplete
        options={options}
        renderInput={(params) => (
          <TextField {...params} size="small" fullWidth sx={inputBoxStyle} />
        )}
        {...props}
      />
    </Box>
  );
};

// Add Invoice Dialog Component
// Add Invoice Dialog Component
const AddInvoiceDialog = ({ open, onClose, details, onSuccess }) => {
  const { showLoader, hideLoader, loading } = useLoading();
  const { showNotification } = useNotification();
  const [load, setLoad] = useState(false);

  const printInvoice = async () => {
    try {
      setLoad(true);
      let temp = await getPrintFormatInvoice({});
      let resp = await printFile(`doctype=Sales Invoice&name=${details?.name}&format=${temp.data.message.result}&no_letterhead=0`);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const blobURL = URL.createObjectURL(blob);
      const newWindow = window.open(blobURL);
      if (newWindow) {
        newWindow.onload = () => {
          // newWindow.print();
        };
      } else {
        console.error('Failed to open new window');
      }

    } catch (err) {
      console.log(err);
    } finally {
      setLoad(false);
    }
  }

  const [unalloc, setUnalloc] = useState(0);
  useEffect(() => {
    getUnallocatedAmount({ reservation: details?.reservation }).then((resp) => {
      setUnalloc(resp.data.message.unallocated);
    }).catch((err) => {
      console.log(err);
    })
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ backgroundColor: "light.main", borderRadius: 3, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={1}>
            <Typography variant="SubTitleBold">Invoice Details</Typography>
            <IconButton onClick={() => printInvoice()} loading={load} color="primary">
              <Print />
            </IconButton>
          </Box>
          <Box display="flex" gap={1}>
            <Typography variant="bodyText1">Invoice TO:</Typography>
            <Typography variant="subHeading">{details?.customer}</Typography>
          </Box>

        </Box>

        <Box display="flex" justifyContent="space-between" mb={2} mt={3.2}>
          <Box display="flex" gap={1}>
            <Typography variant="buttonL">Reservation ID:</Typography>
            <Typography variant="headingLarge">{details?.reservation}</Typography>
          </Box>
          <Typography variant="headingLarge">
            Un-allocated Amount :
            <Box
              component="span"
              ml={1}
              px={1.5}
              py={0.5}
              bgcolor="green.main"
              borderRadius={2}
              color="light.main"
            >
              ₹ {unalloc}
            </Box>
          </Typography>
        </Box>

        <Box border="1px solid #ccc" borderRadius={2} p={1} bgcolor="light.main">
          <Typography variant="headingLarge">Item Invoiced</Typography>
          {details.items.map((ele, idx) => (
            <Box key={idx} display="flex" gap={1} mb={2} alignItems="flex-end">
              <Box flex={2.2}>
                <StyledTextField disabled={true} label="Item" defaultValue={ele.item_name} fullWidth />
              </Box>
              <Box flex={0.7}>
                <StyledTextField
                  disabled={true}
                  type="number"
                  label="Qty"
                  defaultValue={ele.qty.toFixed(2)}
                  fullWidth
                  align="right"  // Right-align quantity
                />
              </Box>
              <Box flex={1.2}>
                <StyledTextField
                  disabled={true}
                  type="number"
                  label="Rate"
                  defaultValue={ele.rate.toFixed(2)}
                  fullWidth
                  align="right"  // Right-align rate
                />
              </Box>
              <Box flex={1.8}>
                <StyledTextField
                  disabled={true}
                  label="Tax"
                  defaultValue={`${Object.values(JSON.parse(ele.item_tax_rate)).reduce((acc, val) => acc + val, 0)} %`}
                  fullWidth
                />
              </Box>
              <Box flex={0.8}>
                <StyledTextField
                  disabled={true}
                  label="Amount"
                  defaultValue={ele.amount.toFixed(2)}
                  fullWidth
                  align="right"  // Right-align amount
                />
              </Box>
            </Box>
          ))}
          <Divider />
          <Box mt={3}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Box sx={{
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                  p: 2,
                  border: '1px solid #eee'
                }}>
                  <Stack spacing={1.5}>
                    {
                      details.taxes.map((ele, ind) => <Box display="flex" justifyContent="space-between" alignItems={"center"}>
                        <Box marginRight={4}>
                          <Typography variant="subHeading">{ele.account_head}:</Typography>
                        </Box>
                        <Typography variant="subHeading" fontWeight="bold">
                          ₹{ele.tax_amount.toFixed(2)}
                        </Typography>
                      </Box>)
                    }


                    <Divider sx={{ my: 1 }} />

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="subHeading" fontWeight="bold">Total Tax:</Typography>
                      <Typography variant="subHeading" fontWeight="bold" color="primary">
                        ₹{details?.total_taxes_and_charges.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box sx={{
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                  p: 2,
                  border: '1px solid #eee'
                }}>
                  <Stack spacing={1.5}>

                    <Box display="flex" justifyContent="space-between" alignItems={"center"}>
                      <Box marginRight={4}>
                        <Typography variant="subHeading">Net Total:</Typography>
                      </Box>
                      <Typography variant="subHeading" fontWeight="bold">
                        ₹{details?.net_total.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="subHeading" fontWeight="bold">Total Tax:</Typography>
                      <Typography variant="subHeading" fontWeight="bold" color="primary">
                        ₹{details?.total_taxes_and_charges.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="subHeading" fontWeight="bold">Discount:</Typography>
                      <Typography variant="subHeading" fontWeight="bold" color="primary">
                        ₹{details?.discount_amount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="subHeading" fontWeight="bold">Total:</Typography>
                      <Typography variant="subHeading" fontWeight="bold" color="primary">
                        ₹{details?.grand_total.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>



        <DialogActions sx={{ justifyContent: "flex-end", mt: 3 }}>
          <Button
            disabled={loading}
            onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
          {
            (!["Paid", "Cancelled", "Draft"].includes(details.status)) && <Button
              disabled={loading || (unalloc < details?.outstanding_amount)}
              variant="contained" sx={{ borderRadius: 2 }}
              onClick={async () => {
                try {
                  showLoader();
                  let res = await markInvoicepayment({
                    sales_invoice: details.name,
                    mode_of_payment: "Cash"
                  });
                  showNotification('success', 'Success', 'Payment Updated Successfully.');
                  onSuccess();
                } catch (err) {
                  showNotification('error', 'Failed', 'Failed to update payment status.');
                  console.log(err);
                } finally {
                  hideLoader();
                }
              }}>
              Mark as Paid
            </Button>
          }

        </DialogActions>
      </Box>
    </Dialog>
  );
};
// Main Invoice List Component
const InvoiceList = ({ addInvoice, invoices, refresh }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const { showLoader, hideLoader } = useLoading();
  const [dialog, setDialog] = useState({ visible: false, details: null });

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          padding: 2,
          backgroundColor: "light.main",
          mt: 2,
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="SubTitleBold">Invoice List</Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "primary.main",
              textTransform: "none",
              borderRadius: 2,
            }}
            // onClick={() => setOpenDialog(true)}
            onClick={addInvoice}
          >
            Add Invoice
          </Button>
        </Box>

        {/* Table */}
        <Box sx={{ overflowX: "auto", borderRadius: "8px" }}>
          <Table sx={{ minWidth: 650 }} stickyHeader>
            <TableHead>
              <TableRow>
                {["Date", "Invoice Number", "Guest / Company", "Invoice Amount", "Status"].map(
                  (text, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        backgroundColor: "#84AFDF",
                        color: "dark.main",
                        fontWeight: 600,
                        py: 1,
                        textAlign:
                          text === "Invoice Amount"
                            ? "right"
                            : text === "Status"
                              ? "center"
                              : "left",
                      }}
                    >
                      {text}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {invoices.map((row, index) => (
                <TableRow key={index} sx={{ cursor: "pointer" }} onClick={async () => {
                  try {
                    showLoader();
                    let details = await getSalesInvoiceDetails(row.name);
                    setDialog({
                      visible: true, details: details.data.data
                    });
                  } catch (err) {
                    console.log(err);
                  } finally {
                    hideLoader();
                  }
                }}>
                  <TableCell sx={{ py: 0.5 }}>{row.posting_date}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{row?.name}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{row?.customer}</TableCell>
                  <TableCell sx={{ py: 0.5, textAlign: "right" }}>₹ {row.grand_total}</TableCell>
                  <TableCell sx={{ py: 0.5, textAlign: "center" }}>
                    <Chip
                      label={row.status}
                      sx={{
                        backgroundColor: row.status === "Paid" ? "green.main" : "#FFA500",
                        color: "white",
                        fontWeight: 600,
                        borderRadius: 1,
                        height: 24,
                        fontSize: 12,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {
                (invoices.length == 0) && <TableRow>
                  <TableCell colSpan={5} align="center">
                    No Invoices found
                  </TableCell>
                </TableRow>
              }
            </TableBody>
          </Table>
        </Box>

        {/* Totals aligned under "Invoice Amount" */}
        {/* <Box mt={2} display="flex" justifyContent="space-around">
          <Box></Box>
          <Box></Box>
          <Box>
            {["Total", "Paid", "Due"].map((label, idx) => (
              <Box
                key={idx}
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
                mt={1}
                ml={10}
              >
                <Typography fontWeight={600}>{label} :</Typography>
                <Box
                  px={2}
                  py={0.5}
                  border="1px solid #ccc"
                  borderRadius={2}
                  minWidth={80}
                  textAlign="center"
                >
                  ₹ 1234
                </Box>
              </Box>
            ))}
          </Box>
        </Box> */}
      </Paper>
      {
        dialog.visible &&
        <AddInvoiceDialog details={dialog.details} open={dialog.visible} onClose={() => { setDialog({ visible: false, details: null }) }} onSuccess={() => { setDialog({ visible: false, details: null }); refresh() }} />
      }  </>
  );
};

export default InvoiceList;